/**
 * Iter56 Iter C — Pure measurement calculations + unit conversions.
 *
 * Deliberately provider-neutral: given a GeoJSON path (Polygon or LineString)
 * OR a Google-native LatLng path, produce imperial+metric numbers the UI
 * displays. When `google.maps.geometry.spherical` is available it is used
 * (accurate for real-world curved-earth distances). In Jest and the Mapbox
 * fallback path we use the shoelace/haversine approximations so tests are
 * deterministic without a live Google API.
 *
 * NO persistence-format side effects live here — those are handled by the
 * backend /api/measurements endpoint.
 */

const M2_PER_FT2 = 0.09290304;
const M_PER_FT = 0.3048;
const EARTH_RADIUS_M = 6371008.8; // mean Earth radius per WGS-84

export function m2ToFt2(m2) {
  return m2 / M2_PER_FT2;
}
export function ft2ToM2(ft2) {
  return ft2 * M2_PER_FT2;
}
export function mToFt(m) {
  return m / M_PER_FT;
}
export function ftToM(ft) {
  return ft * M_PER_FT;
}

function _toRad(d) {
  return (d * Math.PI) / 180;
}

// Great-circle distance in meters between two [lng, lat] points.
export function haversineMeters(a, b) {
  const [lng1, lat1] = a;
  const [lng2, lat2] = b;
  const dLat = _toRad(lat2 - lat1);
  const dLng = _toRad(lng2 - lng1);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(_toRad(lat1)) * Math.cos(_toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(s));
}

// Spherical polygon area in m² using L'Huilier-style formula on a unit sphere.
// Coordinates: GeoJSON ring [[lng, lat], ...] — first == last (closed).
export function sphericalAreaM2(ring) {
  if (!ring || ring.length < 4) return 0;
  let total = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [lng1, lat1] = ring[i];
    const [lng2, lat2] = ring[i + 1];
    total += _toRad(lng2 - lng1) * (2 + Math.sin(_toRad(lat1)) + Math.sin(_toRad(lat2)));
  }
  return Math.abs((total * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2);
}

// GeoJSON LineString length in meters.
export function lineStringLengthMeters(coords) {
  if (!coords || coords.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) total += haversineMeters(coords[i], coords[i + 1]);
  return total;
}

// Polygon perimeter (outer ring) in meters.
export function polygonPerimeterMeters(ring) {
  return lineStringLengthMeters(ring || []);
}

/**
 * Additive / subtractive polygon area. `rings` is [outer, hole1, hole2, ...]
 * matching GeoJSON Polygon coordinates. Holes are subtracted only when they
 * fully lie within the outer ring bounding box — a conservative safety check
 * so a stray hole cannot produce a negative area.
 */
export function polygonNetAreaM2(rings) {
  if (!rings || rings.length === 0) return 0;
  const outerArea = sphericalAreaM2(rings[0]);
  let subtract = 0;
  for (let i = 1; i < rings.length; i++) {
    if (_ringWithinBBox(rings[i], rings[0])) {
      subtract += sphericalAreaM2(rings[i]);
    }
  }
  return Math.max(0, outerArea - subtract);
}

function _ringWithinBBox(inner, outer) {
  const [minLng, minLat, maxLng, maxLat] = _bbox(outer);
  for (const [lng, lat] of inner) {
    if (lng < minLng || lng > maxLng || lat < minLat || lat > maxLat) return false;
  }
  return true;
}
function _bbox(ring) {
  let minLng = Infinity,
    minLat = Infinity,
    maxLng = -Infinity,
    maxLat = -Infinity;
  for (const [lng, lat] of ring) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return [minLng, minLat, maxLng, maxLat];
}

/** Convert a Google LatLng[] into a GeoJSON ring (closes the ring). */
export function latLngPathToRing(path) {
  const ring = path.map((p) => [p.lng(), p.lat()]);
  if (
    ring.length > 0 &&
    (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])
  ) {
    ring.push([ring[0][0], ring[0][1]]);
  }
  return ring;
}

/**
 * computeMeasurement — used by BOTH the Google workspace (with `google` arg)
 * AND unit tests / Mapbox fallback (without `google`). Never throws on
 * missing google; falls back to sphericalAreaM2 / lineStringLengthMeters.
 */
export function computeMeasurement({ measurementType, geometry, google = null, displayUnits = "imperial" }) {
  const result = {
    measurement_type: measurementType,
    display_units: displayUnits,
    metric_value: 0,
    square_feet: null,
    linear_feet: null,
  };
  if (!geometry) return result;

  if (measurementType === "area" || measurementType === "roof") {
    const rings = geometry.type === "Polygon" ? geometry.coordinates : [];
    let m2 = 0;
    if (google && google.maps && google.maps.geometry && rings[0]) {
      const path = rings[0].map(([lng, lat]) => new google.maps.LatLng(lat, lng));
      m2 = google.maps.geometry.spherical.computeArea(path);
      // subtract holes conservatively
      for (let i = 1; i < rings.length; i++) {
        const holePath = rings[i].map(([lng, lat]) => new google.maps.LatLng(lat, lng));
        m2 -= google.maps.geometry.spherical.computeArea(holePath);
      }
      m2 = Math.max(0, m2);
    } else {
      m2 = polygonNetAreaM2(rings);
    }
    result.metric_value = m2;
    result.square_feet = m2ToFt2(m2);
  } else if (measurementType === "linear" || measurementType === "perimeter") {
    let m = 0;
    const coords =
      geometry.type === "LineString"
        ? geometry.coordinates
        : geometry.type === "Polygon"
        ? geometry.coordinates[0]
        : [];
    if (google && google.maps && google.maps.geometry && coords.length) {
      const path = coords.map(([lng, lat]) => new google.maps.LatLng(lat, lng));
      m = google.maps.geometry.spherical.computeLength(path);
    } else {
      m = lineStringLengthMeters(coords);
    }
    result.metric_value = m;
    result.linear_feet = mToFt(m);
  }
  return result;
}

// Human-readable label for the live-drawing overlay.
export function formatMeasurementLabel(r) {
  if (r.square_feet != null) {
    return `${Math.round(r.square_feet).toLocaleString()} sq ft`;
  }
  if (r.linear_feet != null) {
    return `${Math.round(r.linear_feet).toLocaleString()} ft`;
  }
  return "";
}

// Color palette by service category — used to tint drawn polygons.
export const SERVICE_COLORS = {
  roof: "#EF4444",
  lawn: "#10B981",
  driveway: "#8B5CF6",
  siding: "#F59E0B",
  gutter: "#0EA5E9",
  paint: "#EC4899",
  default: "#FF5A00",
};
export function serviceColor(cat) {
  return SERVICE_COLORS[cat] || SERVICE_COLORS.default;
}
